// 現在のアクター（識別情報）を返す Context。
//
// SessionSnapshot から CurrentActor を導出するため、SDK Provider の状態に依存しない。
// アバター画像の idp-file: 解決と data URL 化は本コンポーネント内で実行する。

import { VIEWER_DISPLAY_NAME_MAX_LENGTH } from "@refinement-canvas/domain";
import {
  createMeidenConnectApiAccessTokenProvider,
  resolveIdpFileWithAccessTokenRefresh,
  useMeidenConnectTokenRefreshSignal,
} from "@osakabe/auth-service-provider";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAuthSession } from "./auth-session-context";
import { sanitizeAvatarImageUrl, usePortableAvatarImageUrl } from "./avatar-image-url";
import { getWebConfig } from "../env";

export interface CurrentActor {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarImageUrl?: string | undefined;
  readonly color: string;
}

export interface CurrentActorState {
  readonly actor: CurrentActor;
  readonly status: "loading" | "ready" | "error";
  readonly error?: Error | undefined;
}

interface ProviderProps {
  readonly children: ReactNode;
}

const MOCK_ACTOR: CurrentActor = {
  userId: "mock-canvas-user",
  displayName: "Mock 利用者",
  color: "#1e63d4",
};

const CurrentActorContext = createContext<CurrentActorState | null>(null);

export function MockCurrentActorProvider({ children }: ProviderProps): JSX.Element {
  const state = useMemo<CurrentActorState>(() => ({ actor: MOCK_ACTOR, status: "ready" }), []);
  return <CurrentActorContext.Provider value={state}>{children}</CurrentActorContext.Provider>;
}

// 参照専用の埋め込みモード用アクター。プロフィール/アバター解決（idp-file: 経路）は行わず、
// 静的な値を返す。埋め込みではプレゼンス送信やコメント投稿を行わないため識別情報は最小限でよい。
const EMBED_ACTOR: CurrentActor = {
  userId: "",
  displayName: "",
  color: pickActorColor("embed"),
};

export function EmbedCurrentActorProvider({ children }: ProviderProps): JSX.Element {
  const state = useMemo<CurrentActorState>(() => ({ actor: EMBED_ACTOR, status: "ready" }), []);
  return <CurrentActorContext.Provider value={state}>{children}</CurrentActorContext.Provider>;
}

/**
 * useAuthSession() の SessionSnapshot から CurrentActor を導出するプロバイダー。
 *
 * 旧 RequiredCurrentActorProvider が SDK Provider 配下で `useMeidenConnectUser` を呼んでいた
 * 経路を置き換える。SDK Provider が main tree から外れたため、アバター画像 (idp-file:) の解決は
 * SessionSnapshot の avatarImageUrl と、`@osakabe/auth-service-provider` の
 * `resolveIdpFileWithAccessTokenRefresh` を組み合わせて実行する。
 */
export function SessionBackedCurrentActorProvider({ children }: ProviderProps): JSX.Element {
  const { user } = useAuthSession();
  const config = getWebConfig();
  const serviceOrigin = config.serviceOrigin ?? window.location.origin;

  const apiAccessTokenProvider = useMemo(() => {
    if (config.authServiceOrigin === undefined || user === undefined) {
      return undefined;
    }

    return createMeidenConnectApiAccessTokenProvider({
      authServiceOrigin: config.authServiceOrigin,
      serviceOrigin,
      userId: user.userId,
    });
  }, [config.authServiceOrigin, serviceOrigin, user]);

  const tokenRefreshSignal = useMeidenConnectTokenRefreshSignal();

  // idp-file: アバターは認証サービスへ署名 URL を発行してから取得する必要がある。
  // SDK Provider が main tree に無いため、package が提供する refresh 対応版を直接利用する。
  const resolveIdpFileWithRefresh = useMemo(() => {
    const authServiceOrigin = config.authServiceOrigin;
    if (authServiceOrigin === undefined || apiAccessTokenProvider === undefined) {
      return undefined;
    }

    return (idpFileUrl: string) =>
      resolveIdpFileWithAccessTokenRefresh(idpFileUrl, {
        authServiceOrigin,
        getAccessToken: apiAccessTokenProvider,
      });
  }, [apiAccessTokenProvider, config.authServiceOrigin]);

  const avatarImageUrl = usePortableAvatarImageUrl(
    user?.avatarImageUrl,
    resolveIdpFileWithRefresh,
    tokenRefreshSignal
  );

  const state = useMemo<CurrentActorState>(() => {
    if (user === undefined) {
      // 認証復旧中など SessionSnapshot が無い瞬間は、UI を壊さず最低限の placeholder を返す。
      return {
        actor: {
          userId: "",
          displayName: "",
          color: "#1a1a2e",
        },
        status: "loading",
      };
    }

    const displayName = normalizeCurrentActorDisplayName(user.name, user.email ?? "");
    const actor: CurrentActor = {
      userId: user.userId,
      displayName,
      avatarImageUrl: sanitizeAvatarImageUrl(avatarImageUrl),
      color: pickActorColor(user.userId),
    };
    return { actor, status: "ready" };
  }, [avatarImageUrl, user]);

  return <CurrentActorContext.Provider value={state}>{children}</CurrentActorContext.Provider>;
}

export function useCurrentActorState(): CurrentActorState {
  const state = useContext(CurrentActorContext);
  if (state === null) {
    throw new Error("useCurrentActorState() は CurrentActorProvider 配下で呼び出してください。");
  }
  return state;
}

export function useCurrentActor(): CurrentActor {
  return useCurrentActorState().actor;
}

// 名前から決定論的に色を割り当てる（プレゼンス UI の識別補助）。
function pickActorColor(userId: string): string {
  const palette = ["#1e63d4", "#d22b2b", "#2da55a", "#9b59b6", "#e67e22", "#16a085"];
  let hash = 0;
  for (let i = 0; i < userId.length; i += 1) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length] ?? "#1a1a2e";
}

export function normalizeCurrentActorDisplayName(name: string | undefined, email: string): string {
  const candidate = normalizeDisplayNamePart(name);
  const fallback = normalizeDisplayNamePart(email) || "利用者";
  const displayName = candidate.length > 0 ? candidate : fallback;
  // WebSocket の viewer.sync schema と同じ上限に揃え、長い氏名やメールで送信失敗しないようにする。
  return displayName.slice(0, VIEWER_DISPLAY_NAME_MAX_LENGTH);
}

export function resolveCurrentActorAvatarImageUrl(input: {
  readonly hasProfileRecord: boolean;
  readonly profileAvatarImageUrl: string | null | undefined;
  readonly tokenAvatarImageUrl: string | null | undefined;
}): string | undefined {
  // 認証復旧直後は ID トークン側の iconUrl が空になることがあるため、
  // IdP の User テーブルに最新プロフィールが取れている場合はそちらを正本にする。
  if (input.hasProfileRecord) {
    return normalizeAvatarImageUrl(input.profileAvatarImageUrl);
  }

  return normalizeAvatarImageUrl(input.tokenAvatarImageUrl);
}

function normalizeDisplayNamePart(value: string | undefined): string {
  return value?.trim().replace(/[\u0000-\u001f\u007f]/gu, "") ?? "";
}

function normalizeAvatarImageUrl(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
}
